import React, { useEffect, useState, useRef } from 'react';
import { Formik, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { postShiftEvents, getShiftEventsByID, getShiftLogDetails, updateShiftEvent } from '../../../services/superAdminServices';
import { AlertCircle, X, Clock } from 'lucide-react';

type Props = {
  onClose: () => void;
  selectedShiftId: number;
  onSuccess?: () => void;
  initialValues?: {
    id?: number;
    time: string;
    description: string;
  };
};

// Inline Firefox check — no external file needed
const pad = (n: number) => String(n).padStart(2, '0');

// Converts "H:MM", "HH:MM", or "HH:MM:SS" into total minutes since midnight.
// Returns null if the string can't be parsed, so callers can bail out safely
// instead of falling back to unreliable string comparison.
const toMinutes = (time?: string): number | null => {
  if (!time) return null;
  const match = time.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
};

// Inline dropdown time picker — used only for Firefox
// Application-controlled picker with independently scrollable 00-23 hour and minute columns.
type TimeDropdownProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (value: string) => void;
  initialValue?: string;
  minTime?: string;
  maxTime?: string;
};

const TimeDropdown: React.FC<TimeDropdownProps> = ({ isOpen, onClose, onSelect, initialValue, minTime, maxTime }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const activeHourRef = useRef<HTMLButtonElement>(null);
  const activeMinuteRef = useRef<HTMLButtonElement>(null);

  // Close when clicking outside the dropdown
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  // Scroll the currently selected hour/minute into view when opened
  useEffect(() => {
    if (!isOpen) return;
    activeHourRef.current?.scrollIntoView({ block: 'center' });
    activeMinuteRef.current?.scrollIntoView({ block: 'center' });
  }, [isOpen]);

  if (!isOpen) return null;

  const now = new Date();
  const [currentHour, currentMinute] = (initialValue || `${pad(now.getHours())}:${pad(now.getMinutes())}`).split(':');

  const hours = Array.from({ length: 24 }, (_, h) => pad(h));
  const minutes = Array.from({ length: 60 }, (_, m) => pad(m));

  const minTimeMinutes = toMinutes(minTime);
  const maxTimeMinutes = toMinutes(maxTime);
  // Overnight range: e.g. 23:00 -> 08:00, where start is later in the day than end.
  // A candidate time is valid if it's >= start OR <= end (not the usual AND).
  const isOvernightRange = minTimeMinutes !== null && maxTimeMinutes !== null && minTimeMinutes > maxTimeMinutes;

  const isWithinRange = (candidateMinutes: number | null) => {
    if (candidateMinutes === null) return true;
    if (minTimeMinutes === null && maxTimeMinutes === null) return true;

    if (isOvernightRange) {
      return candidateMinutes >= (minTimeMinutes as number) || candidateMinutes <= (maxTimeMinutes as number);
    }

    if (minTimeMinutes !== null && candidateMinutes < minTimeMinutes) return false;
    if (maxTimeMinutes !== null && candidateMinutes > maxTimeMinutes) return false;
    return true;
  };

  // An hour is fully disabled only if none of its minutes fall within the valid range
  const isHourDisabled = (h: string) => {
    const hourStartMinutes = toMinutes(`${h}:00`);
    const hourEndMinutes = toMinutes(`${h}:59`);
    if (hourStartMinutes === null || hourEndMinutes === null) return false;
    return !isWithinRange(hourStartMinutes) && !isWithinRange(hourEndMinutes);
  };
  // A minute is disabled relative to the currently selected hour
  const isMinuteDisabled = (m: string) => {
    const candidateMinutes = toMinutes(`${currentHour}:${m}`);
    return !isWithinRange(candidateMinutes);
  };

  return (
    <div
      ref={containerRef}
      className="absolute left-0 top-full mt-1 z-50 flex bg-white border border-gray-300 rounded-md shadow-lg overflow-hidden"
    >
      <div className="w-16 max-h-56 overflow-y-auto border-r border-gray-200">
        {hours.map((h) => {
          const active = h === currentHour;
          const disabled = isHourDisabled(h);
          return (
            <button
              key={h}
              ref={active ? activeHourRef : undefined}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(`${h}:${currentMinute}`)}
              className={`block w-full text-center px-3 py-2 text-sm ${
                active ? 'bg-blue-600 text-white' : disabled ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              {h}
            </button>
          );
        })}
      </div>
      <div className="w-16 max-h-56 overflow-y-auto">
        {minutes.map((m) => {
          const active = m === currentMinute;
          const disabled = isMinuteDisabled(m);
          return (
            <button
              key={m}
              ref={active ? activeMinuteRef : undefined}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(`${currentHour}:${m}`)}
              className={`block w-full text-center px-3 py-2 text-sm ${
                active ? 'bg-blue-600 text-white' : disabled ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              {m}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const EventModal: React.FC<Props> = ({ onClose, selectedShiftId, onSuccess, initialValues }) => {
  const [timeSlots, setTimeSlots] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState('');
  const [showError, setShowError] = useState(false);

  const [shiftStartTime, setShiftStartTime] = useState('');
  const [shiftEndTime, setShiftEndTime] = useState('');

  const [timeClockOpen, setTimeClockOpen] = useState(false);

  const formInitialValues = {
    id: initialValues?.id,
    time: initialValues?.time ? initialValues.time.slice(0, 5) : '',
    description: initialValues?.description || '',
  };

  const isEditMode = Boolean(initialValues?.id);

  // Handles overnight shift ranges (e.g. 23:00 -> 08:00) where the start time
  // is numerically later than the end time. In that case a valid selection is
  // anything from start through midnight, OR anything from midnight through end.
  const isTimeBetween = (selectedTime: string, startTime: string, endTime: string) => {
    const selected = toMinutes(selectedTime);
    const start = toMinutes(startTime);
    const end = toMinutes(endTime);

    if (selected === null || start === null || end === null) return true;

    if (start > end) {
      // Overnight range wraps past midnight
      return selected >= start || selected <= end;
    }

    return selected >= start && selected <= end;
  };

  const EventSchema = Yup.object().shape({
    time: Yup.string()
      .required('Time is required')
      .test('between-time', `Time should be between ${shiftStartTime} and ${shiftEndTime}`, function (value) {
        return isTimeBetween(value || '', shiftStartTime, shiftEndTime);
      }),

    description: Yup.string()
      .required('Description is required')
      .test('min-text', 'Description must be at least 10 characters', (value) => {
        const plainText = value?.replace(/<[^>]*>/g, '').trim() || '';
        return plainText.length >= 10;
      })
      .test('max-text', 'Description cannot exceed 1000 characters', (value) => {
        const plainText = value?.replace(/<[^>]*>/g, '').trim() || '';
        return plainText.length <= 1000;
      }),
  });

  useEffect(() => {
    document.body.style.overflow = 'hidden';

    const fetchShiftEvents = async () => {
      try {
        // Existing API
        const response = await getShiftEventsByID(selectedShiftId);

        if (response?.data) {
          const slots = response.data.map((event: any) => event.event_time);
          setTimeSlots(Array.from(new Set(slots)));
        }

        // Get Shift Details
        const shiftResponse = await getShiftLogDetails(selectedShiftId);

        setShiftStartTime(shiftResponse.data.start_time);
        setShiftEndTime(shiftResponse.data.end_time);
      } catch (error: any) {
        console.error('Error fetching shift events:', error);
        const message = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to load shift events';
        setApiError(message);
        setShowError(true);
        setTimeout(() => setShowError(false), 5000);
      } finally {
        setLoading(false);
      }
    };

    fetchShiftEvents();

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [selectedShiftId]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50 overflow-y-auto">
      <div className="bg-white w-[80vw] h-[80vh] rounded-lg shadow-lg p-8 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">
            {isEditMode ? 'Update Time & Event/Incident Description' : 'Add Time & Event/Incident Description'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">
            &times;
          </button>
        </div>

        {/* Formik Form */}
        <Formik
          initialValues={formInitialValues}
          enableReinitialize
          validationSchema={EventSchema}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              const payload = {
                shift_log_id: selectedShiftId,
                event_time: values.time.length === 5 ? `${values.time}:00` : values.time,
                description: values.description,
              };

              if (!isTimeBetween(values.time, shiftStartTime, shiftEndTime)) {
                setApiError(`Time should be between ${shiftStartTime} and ${shiftEndTime}`);

                setShowError(true);
                setSubmitting(false);
                return;
              }

              if (isEditMode && initialValues?.id) {
                await updateShiftEvent(selectedShiftId, initialValues.id, payload); // ← update path
              } else {
                await postShiftEvents(payload); // ← create path
              }
              if (onSuccess) onSuccess();
              onClose();
            } catch (error: any) {
              console.error('Error creating event:', error);
              const message =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                'Failed to add event. Please try again.';
              setApiError(message);
              setShowError(true);
              setTimeout(() => setShowError(false), 5000);
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ values, setFieldValue, isSubmitting }) => (
            <Form className="flex flex-col flex-1 overflow-hidden">
              {/* Main Content */}
              <div className="flex-1 overflow-y-auto flex flex-col pr-2">
                {/* Time Picker */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Time</label>

                  <div className="relative w-[250px]">
                    <button
                      type="button"
                      onClick={() => setTimeClockOpen((open) => !open)}
                      className={`relative w-full text-left border border-gray-300 bg-gray-100 rounded-md p-2 pr-10
                        ${!values.time ? 'text-gray-400' : 'text-gray-700'}
                        outline-none focus:outline-none focus:ring-0 focus:border-gray-300`}
                    >
                      {values.time || 'HH:mm'}
                      <Clock className="absolute right-3 top-2.5 w-4 h-6 text-gray-500" />
                    </button>

                    <TimeDropdown
                      isOpen={timeClockOpen}
                      onClose={() => setTimeClockOpen(false)}
                      initialValue={values.time}
                      minTime={shiftStartTime ? shiftStartTime.slice(0, 5) : undefined}
                      maxTime={shiftEndTime ? shiftEndTime.slice(0, 5) : undefined}
                      onSelect={(time) => setFieldValue('time', time)}
                    />
                  </div>

                  <ErrorMessage name="time" component="div" className="text-red-500 text-sm mt-1" />
                </div>

                {/* Rich Text Editor */}
                <div className="flex-1 flex flex-col min-h-[300px]">
                  <label className="block text-sm font-medium mb-1">Describe Event/Incident/Information</label>
                  <div className="flex-1 border rounded-md bg-gray-100">
                    <ReactQuill
                      value={values.description}
                      onChange={(content) => setFieldValue('description', content)}
                      className="h-full"
                      placeholder="Add description here"
                    />
                  </div>
                  <ErrorMessage name="description" component="div" className="text-red-500 text-sm mt-1" />
                </div>
              </div>

              <div className="border-t border-gray-300 my-6"></div>

              {/* Footer Buttons */}
              <div className="flex justify-end gap-3">
                <button type="button" onClick={onClose} className="bg-gray-100 px-4 py-2 rounded hover:bg-gray-200" disabled={isSubmitting}>
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`px-4 py-2 rounded text-white ${
                    isSubmitting ? 'bg-red-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {isSubmitting ? (isEditMode ? 'Updating...' : 'Adding...') : isEditMode ? 'Update' : 'Add'}
                </button>
              </div>
            </Form>
          )}
        </Formik>

        {/* Error Notification */}
        {showError && (
          <div className="fixed bottom-6 right-6 z-[100]">
            <div className="bg-red-500 text-white rounded-lg shadow-lg p-4 pr-12 min-w-[300px] max-w-[500px] relative animate-slide-in">
              <button
                onClick={() => setShowError(false)}
                className="absolute top-3 right-3 text-white hover:text-gray-200 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold mb-1">Error</h4>
                  <p className="text-sm">{apiError}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventModal;
